<script setup>
import { ref, computed, onMounted } from 'vue';
import OverlayModal from '../OverlayModal.vue';
import PdfPreview from './PdfPreview.vue';
import { useFileActions } from '../../composables/useFileActions.js';
import { fileKind } from '../../domain/fileKind.js';

/* Vorschau eines Dokuments im Dialog: PDF über den vorhandenen Betrachter
   (PdfPreview, erste Seite), Bilder direkt. Der Dateiname wird nicht gezeigt —
   der Titel ist die Dokumentbezeichnung. Alles andere (und jeder Fehler)
   bleibt beim Herunterladen. */
const props = defineProps({
  file: { type: Object, required: true },
  title: { type: String, default: '' },
});
const emit = defineEmits(['close']);

const { hrefFor, download } = useFileActions();
const kind = computed(() => fileKind(props.file));
const imageHref = ref(null);
const imageFailed = ref(false);

onMounted(async () => {
  if (kind.value === 'image') imageHref.value = await hrefFor(props.file);
  if (kind.value === 'image' && !imageHref.value) imageFailed.value = true;
});
</script>

<template>
  <OverlayModal :title="title || $t('files.file')" :width="720" :z="50" @close="emit('close')">
    <div class="fp-body">
      <PdfPreview v-if="kind === 'pdf'" :file="file" />

      <template v-else-if="kind === 'image'">
        <div v-if="imageFailed" class="fp-note">{{ $t('files.previewFailed') }}</div>
        <img v-else-if="imageHref" :src="imageHref" :alt="title" class="fp-image" @error="imageFailed = true" />
        <div v-else class="fp-note">{{ $t('common.loading') }}</div>
      </template>

      <div v-else class="fp-note">{{ $t('files.previewUnsupported') }}</div>
    </div>

    <template #actions>
      <button class="icon-btn" @click="download(file)">{{ $t('files.download') }}</button>
      <button class="btn small" @click="emit('close')">{{ $t('common.close') }}</button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.fp-body {
  padding: 4px 0;
}
.fp-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  border-radius: 8px;
}
.fp-note {
  padding: 28px 0;
  text-align: center;
  color: var(--muted);
  font-size: 0.85rem;
}
</style>
